import { useState, type ReactNode } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EstimateStatusBadge } from './EstimateStatusBadge';
import { SendEstimateDialog } from './SendEstimateDialog';
import { AcceptDeclineDialog } from './AcceptDeclineDialog';
import { EstimateActionConfirmDialog, type EstimateConfirmAction } from './EstimateActionConfirmDialog';
import { useEstimates } from '@/hooks/useEstimates';
import { cn } from '@/lib/utils';
import {
  CANCELLABLE_ESTIMATE_STATUSES,
  canDeleteEstimate,
  type EstimateStatus,
  type EstimateWithRelations,
  type Estimate,
} from '@/types/estimate';
import { Search, MoreHorizontal, Send, Eye, CheckCircle, Clock, Loader2, Ban, Trash2 } from 'lucide-react';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useVisibility } from '@/hooks/useVisibility';
import { formatDocDate } from '@/utils/locale';
import { formatInTz } from '@/utils/timezone';

interface EstimatesTableViewProps {
  onEstimateClick?: (estimateId: string) => void;
  /** When true, only estimates expiring within 3 days are shown (driven by the page banner). */
  expiringOnly?: boolean;
  onExpiringOnlyChange?: (value: boolean) => void;
}

/** Whole calendar days from today (company timezone) until a stored date; date-only values never shift a day. */
const daysUntilInTz = (value: string, timezone: string) => {
  const toUtc = (key: string) => {
    const [y, m, d] = key.split('-').map(Number);
    return Date.UTC(y, m - 1, d);
  };
  const target = formatDocDate(value, 'yyyy-MM-dd', timezone);
  const today = formatInTz(new Date(), timezone, 'yyyy-MM-dd');
  return Math.round((toUtc(target) - toUtc(today)) / 86_400_000);
};

const isExpiringSoon = (estimate: EstimateWithRelations, timezone: string) => {
  if (!estimate.valid_until || !['sent', 'viewed'].includes(estimate.status)) return false;
  const days = daysUntilInTz(estimate.valid_until, timezone);
  return days >= 0 && days <= 3;
};

export function EstimatesTableView({ onEstimateClick, expiringOnly, onExpiringOnlyChange }: EstimatesTableViewProps) {
  const [statusFilter, setStatusFilter] = useState<EstimateStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [acceptDeclineDialogOpen, setAcceptDeclineDialogOpen] = useState(false);
  const [selectedEstimate, setSelectedEstimate] = useState<Estimate | null>(null);
  const [confirmAction, setConfirmAction] = useState<EstimateConfirmAction | null>(null);

  const { data: estimates, isLoading } = useEstimates({
    status: statusFilter,
    search: searchQuery || undefined,
  });

  const { formatMoney: formatCurrency, formatShortDate, timezone } = useCompanyLocale();
  const { canSeeCompanyFinancials } = useVisibility();

  const visibleEstimates = expiringOnly
    ? (estimates || []).filter((estimate) => isExpiringSoon(estimate, timezone))
    : estimates;

  const getExpiryInfo = (validUntil: string | null, status: EstimateStatus) => {
    if (!validUntil || !['sent', 'viewed'].includes(status)) {
      return { dateClass: '', hint: null as ReactNode };
    }

    const daysUntilExpiry = daysUntilInTz(validUntil, timezone);

    if (daysUntilExpiry < 0) {
      return {
        dateClass: 'text-muted-foreground',
        hint: (
          <Badge variant="secondary" className="bg-muted text-muted-foreground">
            Expired
          </Badge>
        ),
      };
    }
    if (daysUntilExpiry <= 3) {
      return {
        dateClass: 'font-semibold text-warning',
        hint: (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-warning">
            <Clock className="h-3 w-3" />
            {daysUntilExpiry === 0 ? 'Today' : `${daysUntilExpiry}d left`}
          </span>
        ),
      };
    }
    return { dateClass: '', hint: null };
  };

  const handleSend = (estimate: Estimate) => {
    setSelectedEstimate(estimate);
    setSendDialogOpen(true);
  };

  const handleAcceptDecline = (estimate: Estimate) => {
    setSelectedEstimate(estimate);
    setAcceptDeclineDialogOpen(true);
  };

  const statusTabs = [
    { value: 'all', label: 'All' },
    { value: 'draft', label: 'Draft' },
    { value: 'sent', label: 'Sent' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'declined', label: 'Declined' },
    { value: 'expired', label: 'Expired' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  return (
    <Card className="overflow-hidden">
      {/* Header: underline status tabs + search */}
      <Tabs
        value={statusFilter}
        onValueChange={(v) => {
          setStatusFilter(v as EstimateStatus | 'all');
          onExpiringOnlyChange?.(false);
        }}
      >
        <div className="flex flex-col gap-2 border-b px-5 pt-2 sm:flex-row sm:items-end sm:justify-between">
          <TabsList className="-mb-px h-auto justify-start gap-4 rounded-none bg-transparent p-0">
            {statusTabs.map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="rounded-none border-b-2 border-transparent bg-transparent px-1 pb-3 pt-2 text-[13px] font-medium text-muted-foreground shadow-none transition-colors hover:text-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <div className="pb-2.5 sm:w-72">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search estimates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 pl-10"
              />
            </div>
          </div>
        </div>
      </Tabs>

      {/* Expiring-only filter strip */}
      {expiringOnly && (
        <div className="flex items-center gap-2 border-b bg-warning/10 px-5 py-2 text-[13px] font-medium text-warning">
          <Clock className="h-3.5 w-3.5 shrink-0" />
          <span>Showing estimates expiring within 3 days</span>
          <button
            type="button"
            onClick={() => onExpiringOnlyChange?.(false)}
            className="ml-auto font-semibold underline-offset-4 hover:underline"
          >
            Show all
          </button>
        </div>
      )}

      {/* Table */}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Estimate #</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead>Service</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Sent</TableHead>
            <TableHead>Expires</TableHead>
            <TableHead className="w-[50px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={8} className="text-center py-8">
                <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
              </TableCell>
            </TableRow>
          ) : !visibleEstimates?.length ? (
            <TableRow>
              <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                No estimates found
              </TableCell>
            </TableRow>
          ) : (
            visibleEstimates.map((estimate) => {
              const expiry = getExpiryInfo(estimate.valid_until, estimate.status);
              return (
                <TableRow
                  key={estimate.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => onEstimateClick?.(estimate.id)}
                >
                  <TableCell className="font-medium text-muted-foreground">{estimate.estimate_number}</TableCell>
                  <TableCell>
                    <div>
                      <span className="block font-medium">{estimate.customer_name?.trim() || '-'}</span>
                      {(estimate.lead?.lead_number || estimate.sent_to_email) && (
                        <p className="text-[11px] text-muted-foreground">
                          {[estimate.lead?.lead_number, estimate.sent_to_email].filter(Boolean).join(' · ')}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {estimate.service_description ? (
                      <span className="block max-w-[240px] truncate">{estimate.service_description}</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-medium tabular-nums">
                    {formatCurrency(estimate.estimate_amount)}
                  </TableCell>
                  <TableCell>
                    <EstimateStatusBadge status={estimate.status} />
                  </TableCell>
                  <TableCell className="text-muted-foreground tabular-nums">
                    {estimate.sent_at ? formatShortDate(estimate.sent_at) : '—'}
                  </TableCell>
                  <TableCell>
                    {estimate.valid_until ? (
                      <div className="flex items-center gap-2">
                        <span className={cn('tabular-nums', expiry.dateClass)}>
                          {formatShortDate(estimate.valid_until)}
                        </span>
                        {expiry.hint}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onEstimateClick?.(estimate.id)}>
                          <Eye className="mr-2 h-4 w-4" />
                          View Details
                        </DropdownMenuItem>
                        {estimate.status === 'draft' && (
                          <DropdownMenuItem onClick={() => handleSend(estimate)}>
                            <Send className="mr-2 h-4 w-4" />
                            Send Estimate
                          </DropdownMenuItem>
                        )}
                        {['sent', 'viewed'].includes(estimate.status) && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleAcceptDecline(estimate)}>
                              <CheckCircle className="mr-2 h-4 w-4" />
                              Mark Accepted/Declined
                            </DropdownMenuItem>
                          </>
                        )}
                        <DropdownMenuSeparator />
                        {CANCELLABLE_ESTIMATE_STATUSES.includes(estimate.status) && (
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedEstimate(estimate);
                              setConfirmAction('cancel');
                            }}
                            className="text-warning focus:text-warning"
                          >
                            <Ban className="mr-2 h-4 w-4" />
                            Cancel Estimate
                          </DropdownMenuItem>
                        )}
                        {canDeleteEstimate(estimate.status, canSeeCompanyFinancials) && (
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedEstimate(estimate);
                              setConfirmAction('delete');
                            }}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete Estimate
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {/* Dialogs */}
      {selectedEstimate && (
        <>
          <SendEstimateDialog
            open={sendDialogOpen}
            onOpenChange={setSendDialogOpen}
            estimate={selectedEstimate}
          />
          <AcceptDeclineDialog
            open={acceptDeclineDialogOpen}
            onOpenChange={setAcceptDeclineDialogOpen}
            estimate={selectedEstimate}
          />
          <EstimateActionConfirmDialog
            action={confirmAction}
            estimate={selectedEstimate}
            onOpenChange={(next) => !next && setConfirmAction(null)}
          />
        </>
      )}
    </Card>
  );
}
